import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { COMMAND_CENTER_MESSAGES_QUERY_KEY } from "@/lib/command-center-realtime";
import {
  currentTenantId,
  lastSeenMessageId,
  mentionCount,
  subscribeToCommandCenterBadges,
  unreadCount,
} from "@/lib/command-center-badges";
import { flattenMessages, type MessagesData } from "./use-command-center-messages-query";

/** Reads the thread from the shared cache; it owns no fetch of its own. */
export function useCommandCenterBadges(): { unread: number; mentions: number } {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [version, setVersion] = useState(0);

  useEffect(() => subscribeToCommandCenterBadges(() => setVersion((value) => value + 1)), []);

  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event?.query.queryKey[0] === "command-center") setVersion((value) => value + 1);
      }),
    [queryClient]
  );

  return useMemo(() => {
    void version;
    const tenantId = currentTenantId();
    if (!tenantId) return { unread: 0, mentions: 0 };
    const data = queryClient.getQueryData<MessagesData>(COMMAND_CENTER_MESSAGES_QUERY_KEY);
    return {
      unread: unreadCount(flattenMessages(data), lastSeenMessageId(tenantId), user?.id ?? null),
      mentions: mentionCount(tenantId),
    };
  }, [queryClient, user?.id, version]);
}
